<template>
  <div
    :class="[
      'container-' + organism.containerWidth,
      organism.removeMargin,
      'organism brand-window-box-overlay flex justify-center items-center py-4 sm:py-28 px-2 sm:px-4 min-h-0 sm:min-h-[623px] relative',
    ]"
  >
    <!-- Background image using <img> tag for better performance -->
    <img
      v-if="organism.backgroundImage"
      :src="getImageSrc(organism.backgroundImage)"
      :loading="slideIndex === 0 ? 'eager' : 'lazy'"
      :fetchpriority="slideIndex === 0 ? 'high' : 'auto'"
      class="absolute top-0 left-0 w-full h-full object-cover -z-1"
      alt="Background"
    />

    <!-- Video background (higher priority than image) -->
    <template v-if="organism.backgroundVideoFile">
      <div class="video-container cursor-default w-full h-full absolute -z-1">
        <video
          ref="videoEl"
          class="absolute top-0 left-0 w-full h-full pointer-events-none object-cover"
          :poster="posterSrc"
          :src="videoSrc"
          preload="none"
          autoplay
          muted
          loop
          playsinline
        ></video>
      </div>
    </template>
    <div
      class="text-center text-white overlay bg-bc-dark-blue/90 max-w-[623px] z-[50]"
    >
      <div
        v-if="organism.text1"
        class="status font-archer text-xl font-bold p-[6px]"
      >
        <span class="drop-shadow-text">{{ organism.text1 }}</span>
      </div>
      <div
        class="flex flex-col justify-center px-8 pt-4 pb-12 sm:px-10 content"
      >
        <div v-if="organism.collectionIcons" class="collections">
          <AtomsCollectionButton
            v-for="icon in organism.collectionIcons"
            :collection="icon"
            class="mr-2"
          />
        </div>
        <h3
          v-if="organism.title3"
          class="font-arial text-[17px] font-bold uppercase text-bc-bright-blue mb-4"
        >
          {{ organism.title3 }}
        </h3>
        <h1
          v-if="organism.title1"
          class="mx-auto text-4xl font-semibold sm:text-6xl font-archer drop-shadow-text"
        >
          <span class="block max-w-sm">{{ organism.title1 }}</span>
        </h1>
        <h2
          v-if="organism.title2"
          class="mx-auto mb-4 text-4xl font-semibold drop-shadow-text font-archer sm:text-6xl"
        >
          {{ organism.title2 }}
        </h2>
        <div
          v-if="organism.description"
          class="max-w-xl mx-auto my-4 text-base content md:text-xl"
          v-html="organism.description"
        ></div>
        <MoleculesButtons
          class="lg:justify-center"
          :buttons="organism"
          :button-colors="buttonColors"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.status {
  background-image: linear-gradient(93deg, #68c2ed 0%, #4dadce 97%);
}
</style>

<script setup>
const props = defineProps({
  organism: Object,
  slideIndex: {
    type: Number,
    default: 0,
  },
});

const videoEl = ref(null);
const videoSrc = ref(null);
const posterSrc = computed(() => {
  if (props.organism.backgroundVideoPoster) {
    return props.organism.backgroundVideoPoster;
  }
  if (props.organism.backgroundImage) {
    return getImageSrc(props.organism.backgroundImage);
  }
  if (props.slideIndex === 0) {
    return "/img/hero-poster.jpg";
  }
  return null;
});

onMounted(() => {
  if (props.organism.containerWidth === "full-width") {
    addFullWidthSupport();
  }

  if (!props.organism.backgroundVideoFile) return;

  // Swiper clones edge slides for infinite-loop continuity. Skip lazy
  // loading on clones so we don't fire a duplicate network request for
  // the same video - the poster still shows during the brief moment a
  // duplicate is visible before Swiper teleports to the real slide.
  if (videoEl.value?.closest?.(".swiper-slide-duplicate")) return;

  const startVideoLoad = () => {
    videoSrc.value = props.organism.backgroundVideoFile;
    nextTick(() => {
      if (videoEl.value && typeof videoEl.value.load === "function") {
        videoEl.value.load();
      }
    });
  };

  if (typeof window !== "undefined" && "requestIdleCallback" in window) {
    window.requestIdleCallback(startVideoLoad, { timeout: 2000 });
  } else {
    setTimeout(startVideoLoad, 0);
  }
});

const buttonColors = computed(() => {
  const buttonColorsArray = [];
  if (props.organism.button1Color) {
    buttonColorsArray.push(props.organism.button1Color);
  }
  if (props.organism.button2Color) {
    buttonColorsArray.push(props.organism.button2Color);
  }
  if (props.organism.button3Color) {
    buttonColorsArray.push(props.organism.button3Color);
  }
  return buttonColorsArray;
});
</script>
